
import { useState, useCallback, memo, useEffect, useRef, type FocusEvent, type MouseEvent } from 'react';
import { Menu, X, Monitor, Shield, Cloud, Network, Cable, Lock, ChevronDown, MapPin, ArrowRight, Phone, Zap, Wifi, AlertTriangle, Camera, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TrackedPhoneLink } from '../ui/TrackedPhoneLink';

// Constants for better maintainability
type NavItem = { name: string; icon: LucideIcon; href: string; description: string; featured?: boolean };

const DROPDOWN_CLOSE_DELAY_MS = 100;

export const Header = memo(function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [logoError, setLogoError] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Handle escape key for mobile menu and dropdowns
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isMenuOpen) {
          setIsMenuOpen(false);
          menuButtonRef.current?.focus();
        } else if (activeDropdown) {
          setActiveDropdown(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen, activeDropdown]);

  // Focus management for mobile menu
  useEffect(() => {
    if (isMenuOpen && mobileMenuRef.current) {
      const firstLink = mobileMenuRef.current.querySelector('a, button');
      if (firstLink instanceof HTMLElement) {
        firstLink.focus();
      }
    }
  }, [isMenuOpen]);

  const handleLinkClick = useCallback(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, []);

  const handleMouseEnter = useCallback((dropdown: string) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setActiveDropdown(dropdown);
  }, [hoverTimeout]);

  const handleMouseLeave = useCallback(() => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, DROPDOWN_CLOSE_DELAY_MS);
    setHoverTimeout(timeout);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout]);

  const serviceCategories: Record<string, NavItem[]> = {
    'Cabling & Fiber': [
      { name: 'Structured Cabling & Fiber', icon: Cable, href: '/services/structured-cabling', description: 'Copper and fiber, installed by our own crew', featured: true },
      { name: 'Network Rack Installation', icon: Network, href: '/services/network-rack-installation', description: 'Racks, patch panels and cable management' },
      { name: 'Meridian Low Voltage Contractor', icon: MapPin, href: '/locations/meridian-low-voltage-contractor', description: 'Based on E Territory Dr in Meridian' }
    ],
    'Cameras & Access Control': [
      { name: 'Security Cameras', icon: Camera, href: '/services/security-cameras', description: 'UniFi Protect camera systems', featured: true },
      { name: 'Access Control', icon: Lock, href: '/services/access-control', description: 'UniFi Access & PDK, door hardware', featured: true },
      { name: 'Camera Installation Guide', icon: Camera, href: '/services/cameras/installation-guide', description: 'Camera types and placement' },
      { name: 'PoE Technical Guide', icon: Network, href: '/services/cameras/poe-technical-guide', description: 'Powering cameras over the network' },
      { name: 'Camera Planning Guide', icon: Camera, href: '/services/cameras/costs-roi-guide', description: 'What to plan for before you buy' }
    ],
    'Wi-Fi & Networks': [
      { name: 'Business Wi-Fi & UniFi', icon: Wifi, href: '/services/unifi-installation', description: 'Access points, switches and gateways', featured: true },
      { name: 'Network Installation', icon: Network, href: '/services/network', description: 'Business network setup', featured: true },
      { name: 'UniFi vs Meraki', icon: Zap, href: '/unifi-vs-meraki', description: 'Side-by-side comparison' },
      { name: 'VoIP Phone Systems', icon: Phone, href: '/services/voip-phone-systems', description: 'Business phone systems' }
    ],
    'Managed IT': [
      { name: 'IT Support', icon: Monitor, href: '/services/it-support', description: 'Help desk and monitoring' },
      { name: 'Cybersecurity', icon: Shield, href: '/services/cybersecurity', description: 'Threat protection for your business' },
      { name: 'Microsoft 365', icon: Cloud, href: '/services/microsoft-365', description: 'Email, Teams and file sharing' },
      { name: 'Managed Network', icon: Network, href: '/managed-network', description: 'Network equipment plus ongoing management' },
      { name: 'Emergency IT Support', icon: AlertTriangle, href: '/services/emergency-it-support', description: 'Help when something breaks' }
    ]
  };

  const locations = [
    { name: 'Meridian', href: '/location/meridian' },
    { name: 'South Meridian (Network & IT)', href: '/locations/south-meridian-network-cabling-managed-it' },
    { name: 'Boise', href: '/location/boise' },
    { name: 'SE Boise (Wi-Fi & VoIP)', href: '/locations/se-boise-business-wifi-voip' },
    { name: 'Nampa', href: '/location/nampa' },
    { name: 'Eagle', href: '/location/eagle' },
    { name: 'Caldwell', href: '/location/caldwell' },
    { name: 'Kuna', href: '/location/kuna' }
  ];

  // Dropdowns stay in the DOM so their links are in the prerendered HTML; CSS shows and hides them.
  // "invisible" also takes the hidden links out of the tab order and the accessibility tree.
  const dropdownVisibility = (name: string) =>
    activeDropdown === name
      ? 'visible opacity-100 pointer-events-auto'
      : 'invisible opacity-0 pointer-events-none';

  // Keyboard activation (Enter/Space, detail === 0) toggles the menu. A mouse or touch click only opens it,
  // so a click right after hover-open does not close it again.
  const handleDropdownClick = useCallback((dropdown: string, e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail === 0) {
      setActiveDropdown((current) => (current === dropdown ? null : dropdown));
    } else {
      setActiveDropdown(dropdown);
    }
  }, []);

  const handleDropdownBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) {
      setActiveDropdown(null);
    }
  }, []);

  return (
    <header className="fixed top-0 w-full z-50 transition-all duration-300 bg-white/90 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            onClick={handleLinkClick}
            className="flex items-center space-x-2 z-10 focus:outline-none rounded-lg"
            aria-label="XXL IT Services - Home"
          >
            {!logoError ? (
              <img
                src="/logo.svg"
                alt="XXL IT Services"
                className="h-10 w-auto"
                width="120"
                height="40"
                onError={() => setLogoError(true)}
                loading="eager"
              />
            ) : (
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-cobalt-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">XXL</span>
                </div>
                <span className="text-xl font-bold text-gray-900">
                  IT Services
                </span>
              </div>
            )}
          </Link>

          {/* Brand Promise Tagline - Desktop Only */}
          <div className="hidden xl:flex items-center ml-6">
            <div className="flex items-center bg-gray-50 border border-gray-100 rounded-full px-4 py-1.5">
              <Cable className="w-4 h-4 text-cobalt-600 mr-2" />
              <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                Commercial Low Voltage Contractor
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className="text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm"
            >
              Home
            </Link>

            <Link
              to="/about"
              className="text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm"
            >
              About
            </Link>

            <Link
              to="/faq"
              className="text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm"
            >
              FAQ
            </Link>

            {/* Services Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
              onBlur={handleDropdownBlur}
              role="navigation"
            >
              <button
                type="button"
                onClick={(e) => handleDropdownClick('services', e)}
                aria-controls="services-menu"
                className={`flex items-center text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm ${activeDropdown === 'services' ? 'bg-gray-100 text-cobalt-600' : ''}`}
                aria-expanded={activeDropdown === 'services'}
                aria-haspopup="true"
              >
                Services
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              <div
                  id="services-menu"
                  className={`absolute top-full right-[-100px] w-[800px] bg-white mt-4 rounded-3xl shadow-2xl p-8 border border-gray-100 z-50 transition-opacity duration-150 ${dropdownVisibility('services')}`}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  role="menu"
                >
                  <div className="absolute top-0 right-[140px] w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45 -translate-y-1/2"></div>

                  <div className="grid grid-cols-2 gap-8">
                    {Object.entries(serviceCategories).map(([category, services], idx) => (
                      <div key={category} className={`${idx > 1 ? 'col-span-1' : ''}`}>
                        <h3 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-widest flex items-center">
                          {idx === 0 && <Cable className="w-3 h-3 mr-2" />}
                          {idx === 1 && <Lock className="w-3 h-3 mr-2" />}
                          {idx === 2 && <Wifi className="w-3 h-3 mr-2" />}
                          {idx === 3 && <Monitor className="w-3 h-3 mr-2" />}
                          {category}
                        </h3>
                        <div className="space-y-1">
                          {services.map((service) => {
                            const Icon = service.icon;
                            const isFeatured = service.featured;
                            return (
                              <Link
                                key={service.href}
                                to={service.href}
                                onClick={handleLinkClick}
                                className="group flex items-start space-x-3 p-3 rounded-xl transition-all hover:bg-gray-50"
                              >
                                <div className={`p-2 rounded-lg ${isFeatured ? 'bg-cobalt-50 text-cobalt-600' : 'bg-gray-100 text-gray-500'} group-hover:bg-cobalt-600 group-hover:text-white transition-colors`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-gray-700 group-hover:text-cobalt-600">
                                    {service.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                    {service.description}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 mt-6 pt-4 flex justify-between items-center bg-gray-50 -mx-8 -mb-8 p-6 rounded-b-3xl">
                    <span className="text-sm text-gray-500">Commercial work only, across the Treasure Valley</span>
                    <Link
                      to="/services"
                      onClick={handleLinkClick}
                      className="inline-flex items-center text-cobalt-600 hover:text-cobalt-700 font-medium text-sm transition-colors"
                    >
                      View all services
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
            </div>

            {/* Locations Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('locations')}
              onMouseLeave={handleMouseLeave}
              onBlur={handleDropdownBlur}
              role="navigation"
            >
              <button
                type="button"
                onClick={(e) => handleDropdownClick('locations', e)}
                aria-controls="locations-menu"
                className={`flex items-center text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm ${activeDropdown === 'locations' ? 'bg-gray-100 text-cobalt-600' : ''}`}
                aria-expanded={activeDropdown === 'locations'}
                aria-haspopup="true"
              >
                Locations
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === 'locations' ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              <div
                  id="locations-menu"
                  className={`absolute top-full left-0 w-64 bg-white mt-4 rounded-3xl shadow-xl p-4 border border-gray-100 z-50 transition-opacity duration-150 ${dropdownVisibility('locations')}`}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  role="menu"
                >
                  <div className="absolute top-0 left-8 w-4 h-4 bg-white border-l border-t border-gray-100 transform rotate-45 -translate-y-1/2"></div>
                  <div className="space-y-1">
                    {locations.map((location) => (
                      <Link
                        key={location.href}
                        to={location.href}
                        onClick={handleLinkClick}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl text-gray-600 hover:text-cobalt-600 transition-colors"
                      >
                        <MapPin className="w-4 h-4 text-cobalt-500" />
                        <span className="text-sm font-medium">{location.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
            </div>

            <Link
              to="/blog"
              className="text-gray-600 hover:text-cobalt-600 px-4 py-2 rounded-full hover:bg-gray-100/50 transition-colors font-medium text-sm"
            >
              Blog
            </Link>

            <Link
              to="/partners"
              className="text-cobalt-600 hover:text-cobalt-700 px-4 py-2 rounded-full hover:bg-cobalt-50 transition-colors font-medium text-sm border border-cobalt-100"
            >
              Partners
            </Link>

            <TrackedPhoneLink
              location="header_desktop"
              className="hidden lg:flex items-center text-gray-600 hover:text-cobalt-600 text-sm font-medium"
            >
              <Phone className="w-4 h-4 mr-1" />
              (208) 328-4500
            </TrackedPhoneLink>

            <Link
              to="/contact"
              className="ml-4 bg-gradient-to-r from-cobalt-600 to-cobalt-500 hover:from-cobalt-500 hover:to-cobalt-400 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-lg hover:shadow-cobalt-500/25 hover:-translate-y-0.5"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-4">
            <TrackedPhoneLink
              location="header_mobile"
              className="lg:hidden p-2 text-cobalt-600 rounded-lg hover:bg-gray-100"
            >
              <Phone size={24} />
            </TrackedPhoneLink>
            <button
              ref={menuButtonRef}
              className="text-gray-600 p-2 focus:outline-none rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-100 h-[calc(100vh-72px)] overflow-y-auto"
            role="navigation"
          >
            <div className="p-6 space-y-8 pb-24">
              {/* Quick Links */}
              <div className="grid grid-cols-2 gap-4">
                <Link to="/contact" onClick={handleLinkClick} className="col-span-2 bg-cobalt-600 text-white p-4 rounded-xl text-center font-bold text-lg mb-2 shadow-lg shadow-cobalt-200">
                  Get a Quote
                </Link>
                <Link to="/" onClick={handleLinkClick} className="bg-gray-50 p-4 rounded-xl text-center text-gray-700 font-medium">Home</Link>
                <Link to="/about" onClick={handleLinkClick} className="bg-gray-50 p-4 rounded-xl text-center text-gray-700 font-medium">About</Link>
                <Link to="/faq" onClick={handleLinkClick} className="bg-gray-50 p-4 rounded-xl text-center text-gray-700 font-medium">FAQ</Link>
                <Link to="/partners" onClick={handleLinkClick} className="bg-gray-50 p-4 rounded-xl text-center text-gray-700 font-medium">Partners</Link>
              </div>

              {/* Services Accordion Style */}
              {Object.entries(serviceCategories).map(([category, services]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">
                    {category}
                  </h3>
                  <div className="space-y-1">
                    {services.map((service) => {
                      const Icon = service.icon;
                      return (
                        <Link
                          key={service.href}
                          to={service.href}
                          onClick={handleLinkClick}
                          className="flex items-center space-x-3 p-3 rounded-xl active:bg-gray-100 text-gray-600"
                        >
                          <Icon className="w-5 h-5 text-cobalt-600" />
                          <span className="font-medium text-sm">{service.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="pt-8 border-t border-gray-100 text-center">
                <p className="text-gray-400 text-sm mb-4">Serving Treasure Valley</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {locations.map(loc => (
                    <Link key={loc.href} to={loc.href} onClick={handleLinkClick} className="text-xs px-3 py-1 bg-gray-100 rounded-full text-gray-500">
                      {loc.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
})